import { Button, Form, Input, message, Table, TableColumnsType, Tag, Typography } from 'antd'
import clsx from 'clsx'
import { StakingTokenType } from 'Modules'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import ButtonCopy from 'component/button/button-copy'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { StakingPoolTypeLabel, StakingTokenTypeLabel } from 'constants/staking'
import { PAGES } from 'routes'
import { convertDateToString } from 'utils/format-time'
import { logError } from 'utils/logger'
import { getObjectToValue } from 'utils/object'

import ModalCreateOrUpdate from './modal-create-or-update'
import Overview from './overview'

import './style.scss'

const { Text } = Typography

const Staking = () => {
  const navigate = useNavigate()

  const [form] = Form.useForm()

  const [filterForm] = Form.useForm()

  const [dataStaking, setDataStaking]: any = useState([])

  const [filter, setFilter] = useState<any>({})

  const [pagination, setPagination] = useState({
    page: DEFAULT_PAGE_INDEX,
    limit: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState<number>(0)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [modalMode, setModalMode] = useState('create')

  const [_, contextHolder] = message.useMessage()

  const [poolContract, setPoolContract] = useState('')

  useEffect(() => {
    getDataStaking({ ...pagination, ...filter, contractAddress: poolContract })
  }, [pagination, filter, poolContract])

  const getDataStaking = async (payload: any) => {
    try {
      const res = await AppAdmin.getListStakingToken(payload)
      if (res?.data.success === true) {
        const results = res?.data.data?.results
        setDataStaking(results || [])
        setTotal(res?.data.data?.totalResults)
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const showEditModal = async (record: any) => {
    setIsModalOpen(true)
    setModalMode('update')
    form.setFieldsValue({ ...record, poolType: record.poolClassify })
  }

  const handleClickCreate = () => {
    form.resetFields() // Clear form fields
    setModalMode('create') // Set mode to create
    setIsModalOpen(true)
  }

  const onSubmit = async () => {
    await getDataStaking({ ...pagination, ...filter })
  }

  const columns: TableColumnsType<StakingTokenType> = [
    {
      title: 'Pool Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Button
          type="link"
          block
          color="primary"
          className="button-pool-name"
          onClick={() => {
            navigate(`${PAGES.STAKING_TOKEN}/${record?._id}`)
          }}
        >
          <Text style={{ width: 150 }} ellipsis={{ tooltip: text }} className="text-pool-name">
            {text}
          </Text>
        </Button>
      ),
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <LinkAddress address={text} />
          {text ? <ButtonCopy text={text} /> : null}
        </div>
      ),
    },
    {
      title: 'Pool Type',
      dataIndex: 'poolClassify',
      key: 'poolClassify',
      render: (poolClassify, record) => {
        const type = poolClassify ?? (record.compoundInterval ? 1 : 0)
        const obj = getObjectToValue(StakingPoolTypeLabel, type)
        return <Tag color={type === 0 ? 'blue' : 'purple'}>{obj?.label || ''}</Tag>
      },
    },
    {
      title: 'Period',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const objType = getObjectToValue(StakingTokenTypeLabel, type)
        return (
          <span className={clsx({ [`${objType?.labelClassName}`]: true })}>
            {objType?.label || ''}
          </span>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (status) =>
        status ? (
          <span className="status-success">Enable</span>
        ) : (
          <span className="status-fail">Disable</span>
        ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => convertDateToString(text),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <div className="btn-group">
          <Button
            className="button-detail"
            onClick={() => {
              navigate(`${PAGES.STAKING_TOKEN}/${record?._id}`)
            }}
          >
            Detail
          </Button>

          <ProtectedButton
            className="button-edit"
            feature={FEATURE.STAKING_TOKEN}
            requireCRUD
            onClick={() => showEditModal(record)}
          >
            Edit
          </ProtectedButton>
        </div>
      ),
    },
  ]

  const additionalItems = [BREADCRUMBS.STAKING_TOKEN]

  return (
    <>
      {contextHolder}
      <div className="main-section staking">
        <Breadcrumb additionalItems={additionalItems} />
        <Overview />
        <div className="main-title">
          Staking Token
          <span className="new-button">
            <ProtectedButton
              type="primary"
              feature={FEATURE.STAKING_TOKEN}
              requireCRUD
              onClick={handleClickCreate}
            >
              New Pool
            </ProtectedButton>
          </span>
        </div>

        <div className="table-row">
          <div className="main-table-row">
            <div>
              <Form
                form={filterForm}
                layout="inline"
                onFinish={(values) => {
                  setFilter(values)
                  setPagination({ ...pagination, page: DEFAULT_PAGE_INDEX })
                }}
                style={{ marginTop: 10, marginBottom: 20 }}
              >
                <Form.Item label="Contract Address" name="poolContract">
                  <Input
                    allowClear
                    placeholder="Enter Contract Address"
                    style={{ width: '100%' }}
                    value={poolContract}
                    onChange={(e) => {
                      setPoolContract(e.target.value)
                    }}
                  />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    Search
                  </Button>
                </Form.Item>
              </Form>
            </div>
            <Table<StakingTokenType>
              rowKey="_id"
              columns={columns}
              dataSource={dataStaking}
              className="main-table"
              scroll={{ x: 1000 }}
              pagination={
                total <= 10
                  ? false
                  : {
                      current: pagination.page,
                      pageSize: pagination.limit,
                      pageSizeOptions: DEFAULT_PAGE_OPTIONS,
                      total: total,
                      showSizeChanger: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                      onShowSizeChange(current, pageSize) {
                        setPagination({
                          page: current,
                          limit: pageSize,
                        })
                      },
                      onChange(page, pageSize) {
                        setPagination({
                          page: page,
                          limit: pageSize,
                        })
                      },
                    }
              }
            />
            <ModalCreateOrUpdate
              modalMode={modalMode}
              form={form}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              onSubmit={onSubmit}
            />
          </div>
        </div>
      </div>
    </>
  )
}
export default Staking
